import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Copy, MessageSquare, Pin, Search, Sparkles } from "lucide-react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import {
  addClientNoteToRef,
  buildClientNotesRef,
  fetchClientWorkspacesForNotes,
  fetchDocumentMeta,
  fetchDocumentsMetaBatch,
  fetchNotesPage,
  toggleNotePinned,
  addNoteReply,
} from "../../../services/ClientNotesTabRepository";
import { FIREBASE_CONFIG, LAW_FIRM_INFO } from "../../../config/Config";
import { generateTextWithGemini } from "../../../services/LegalAPIs";
import { requestDocumentConsent } from "../../../utils/documentConsent";
import { resolveDocumentUrl } from "../../../utils/documentResolver";
import {
  queuePublishedDocumentEdit,
  resolvePublishedDocumentEditTarget,
} from "../../../utils/publishedDocumentEditor";
import {
  buildLatestPublicationVersionBySeries,
  buildPublicationVersionState,
  buildPublishedDocumentActionState,
} from "../../../utils/prontuarioPublicationSSOT";

const PAGE_SIZE = 40;
const AI_SUMMARY_MAX_NOTES = 15;
const AI_SUMMARY_SYSTEM_PROMPT =
  "Atue como um Advogado Sênior. Abaixo estão as notas do prontuário de um cliente. Redija um 'Sumário Executivo' altamente profissional, em um único parágrafo fluido, relatando o status atual do caso e os próximos passos, para ser enviado diretamente para o cliente leigo ler no WhatsApp. Não invente dados e não use jargões excessivos.";

const normalizeWorkspaceLabel = (label) => {
  const raw = String(label || "").trim();
  if (!raw) return "";
  const head = raw.split("•")[0];
  return String(head || "").trim();
};

const titleCaseWords = (value) =>
  String(value || "")
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");

const escapeHtml = (value) =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

export const ClientNotesTab = ({ clientId }) => {
  const id = useMemo(() => String(clientId || "").trim(), [clientId]);

  const [editorFocused, setEditorFocused] = useState(false);
  const [editorHasContent, setEditorHasContent] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [lastDocSnap, setLastDocSnap] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  const [auditWorkspaceId, setAuditWorkspaceId] = useState("__ALL__");
  const [auditType, setAuditType] = useState("ALL");
  const [auditSearch, setAuditSearch] = useState("");
  const [sortKey, setSortKey] = useState("DT");
  const [sortDir, setSortDir] = useState("desc");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const [replyDraft, setReplyDraft] = useState("");
  const [replySaving, setReplySaving] = useState(false);
  const [resolvedEventUrl, setResolvedEventUrl] = useState("");
  const [resolvingEventUrl, setResolvingEventUrl] = useState(false);
  const [documentMetaById, setDocumentMetaById] = useState({});
  const [consentSaving, setConsentSaving] = useState(false);
  const [consentFeedback, setConsentFeedback] = useState("");
  const [aiSummary, setAiSummary] = useState("");
  const [isSummarizing, setIsSummarizing] = useState(false);
  const [aiSummaryError, setAiSummaryError] = useState("");

  const [workspaceIdDraft, setWorkspaceIdDraft] = useState("");
  const [workspacesLoading, setWorkspacesLoading] = useState(false);

  const replyTextareaRef = useRef(null);
  const [workspaces, setWorkspaces] = useState([]);

  const workspacesLoadingRef = useRef(false);
  const latestClientIdRef = useRef(id);
  const documentMetaRef = useRef({});
  const documentLoadBusyRef = useRef(false);

  useEffect(() => {
    if (!flyoutOpen) return;

    const onKeyDownCapture = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setFlyoutOpen(false);
      }
    };

    window.addEventListener("keydown", onKeyDownCapture, true);
    return () => window.removeEventListener("keydown", onKeyDownCapture, true);
  }, [flyoutOpen]);

  useEffect(() => {
    if (!flyoutOpen) return;
    const t = setTimeout(() => {
      const el = replyTextareaRef.current;
      if (!el) return;
      if (typeof el.focus !== "function") return;
      el.focus();
    }, 0);
    return () => clearTimeout(t);
  }, [flyoutOpen, selectedEvent]);

  useEffect(() => {
    if (flyoutOpen) return;
    if (!selectedEvent) return;
    const t = setTimeout(() => setSelectedEvent(null), 200);
    return () => clearTimeout(t);
  }, [flyoutOpen, selectedEvent]);

  useEffect(() => {
    latestClientIdRef.current = id;
  }, [id]);

  useEffect(() => {
    let active = true;

    const documentId = String(selectedEvent?.documentId || "").trim();
    const baseMeta = documentId ? documentMetaById?.[documentId] || {} : {};

    if (!selectedEvent) {
      setResolvedEventUrl("");
      setResolvingEventUrl(false);
      return () => {
        active = false;
      };
    }

    setResolvedEventUrl("");
    setResolvingEventUrl(true);

    const resolveSelectedDocumentUrl = async () => {
      try {
        let docData = {
          ...(baseMeta || {}),
          storageEngine:
            baseMeta?.storageEngine || selectedEvent?.storageEngine || null,
          storagePath: String(
            baseMeta?.storagePath || selectedEvent?.storagePath || "",
          ).trim(),
          storage_path: String(
            baseMeta?.storage_path ||
              selectedEvent?.storage_path ||
              selectedEvent?.event?.payload?.storage_path ||
              "",
          ).trim(),
          fileUrl: String(
            baseMeta?.fileUrl ||
              selectedEvent?.fileUrl ||
              selectedEvent?.event?.payload?.fileUrl ||
              "",
          ).trim(),
          url: String(
            baseMeta?.url || selectedEvent?.event?.payload?.url || "",
          ).trim(),
          downloadUrl: String(
            baseMeta?.downloadUrl ||
              selectedEvent?.event?.payload?.downloadUrl ||
              "",
          ).trim(),
          mirror_url: String(
            baseMeta?.mirror_url ||
              selectedEvent?.event?.payload?.mirror_url ||
              "",
          ).trim(),
          webViewLink: String(baseMeta?.webViewLink || "").trim(),
          webContentLink: String(baseMeta?.webContentLink || "").trim(),
        };

        if (documentId && Object.keys(baseMeta || {}).length === 0) {
          docData = await fetchDocumentMeta(documentId);
        }

        const nextUrl = String(await resolveDocumentUrl(docData)).trim();
        if (active) setResolvedEventUrl(nextUrl);
      } catch (e) {
        void e;
        if (active) setResolvedEventUrl("");
      } finally {
        if (active) setResolvingEventUrl(false);
      }
    };

    resolveSelectedDocumentUrl();

    return () => {
      active = false;
    };
  }, [documentMetaById, selectedEvent]);

  const notesRef = useMemo(() => {
    if (!id) return null;
    return buildClientNotesRef(id);
  }, [id]);

  const workspacesById = useMemo(() => {
    const m = new Map();
    (Array.isArray(workspaces) ? workspaces : []).forEach((w) => {
      const wid = String(w?.id || "").trim();
      if (!wid) return;
      const title =
        String(w?.pieceType || w?.title || "Processo").trim() || "Processo";
      m.set(wid, title);
    });
    return m;
  }, [workspaces]);

  const loadWorkspaces = useCallback(async () => {
    const cid = String(id || "").trim();
    if (!cid) {
      setWorkspaces([]);
      setWorkspacesLoading(false);
      workspacesLoadingRef.current = false;
      return;
    }
    if (workspacesLoadingRef.current) return;

    const token = cid;
    const alive = true;
    workspacesLoadingRef.current = true;
    setWorkspacesLoading(true);
    try {
      const out = await fetchClientWorkspacesForNotes(cid);
      if (!alive) return;
      setWorkspaces(out);
    } catch (e) {
      if (!alive) return;
      console.warn("[ClientNotesTab] Workspaces error:", e);
    } finally {
      workspacesLoadingRef.current = false;
      if (alive) setWorkspacesLoading(false);
    }
  }, [id]);

  const formatGridDt = useCallback((dt) => {
    try {
      if (!dt) return "";
      const date = dt.toLocaleDateString("pt-BR");
      const time = dt.toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
      });
      return `${date} ${time}`;
    } catch {
      return "";
    }
  }, []);

  const normalizeEvent = useCallback((row) => {
    const raw = row || {};
    const categoryRaw = String(raw?.category || "")
      .trim()
      .toUpperCase();
    const category =
      categoryRaw === "SYSTEM"
        ? "SYSTEM"
        : categoryRaw === "USER"
          ? "USER"
          : "";
    const type = String(raw?.type || "").trim() || "";
    const payload =
      raw?.payload && typeof raw.payload === "object" ? raw.payload : {};
    const actor = raw?.actor && typeof raw.actor === "object" ? raw.actor : {};

    if (category && actor && (payload || type)) {
      const rawActorName = String(actor?.name || "").trim();
      const actorName =
        rawActorName === "Operador" && category === "USER"
          ? "Rodrigo Demeterco"
          : rawActorName ||
            (category === "SYSTEM" ? "Sistema" : "Rodrigo Demeterco");
      const actorIsBot = Boolean(actor?.isBot);
      return {
        category,
        type,
        payload,
        actor: { name: actorName, isBot: actorIsBot },
      };
    }

    const legacyText = String(raw?.text || "").trim();
    const legacyType = String(raw?.type || "")
      .trim()
      .toUpperCase();
    const legacyAuthor = String(raw?.author || "").trim();

    if (legacyType === "SYSTEM_DOC") {
      const prefix = "Documento anexado:";
      const inferredFileName = legacyText
        .toLowerCase()
        .startsWith(prefix.toLowerCase())
        ? legacyText.slice(prefix.length).trim()
        : "";

      return {
        category: "SYSTEM",
        type: "DOC_UPLOADED",
        payload: {
          fileName: inferredFileName || undefined,
          text: legacyText || undefined,
        },
        actor: { name: legacyAuthor || "Sistema", isBot: true },
      };
    }

    return {
      category: "USER",
      type: "USER_NOTE_ADDED",
      payload: { text: legacyText || undefined },
      actor: { name: legacyAuthor || "Advogado", isBot: false },
    };
  }, []);

  const humanizeActorName = useCallback((actorName, category) => {
    const raw = String(actorName || "").trim();
    if (!raw) return category === "SYSTEM" ? "Sistema" : "Operador";

    const officeEmail = String(LAW_FIRM_INFO?.contact?.email || "")
      .trim()
      .toLowerCase();
    const officeName = String(LAW_FIRM_INFO?.name || "").trim();
    const officeFirstName = String(officeName.split(/\s+/)[0] || "")
      .trim()
      .toLowerCase();
    const rawLower = raw.toLowerCase();

    if (rawLower === "operador" || rawLower === "advogado")
      return officeName || "Operador";
    if (rawLower === "sistema") return "Sistema";
    if (officeEmail && rawLower === officeEmail) return officeName || raw;
    if (officeFirstName && rawLower === officeFirstName)
      return officeName || raw;

    if (raw.includes("@")) {
      const emailUser = String(raw.split("@")[0] || "").trim();
      if (emailUser && officeEmail && rawLower === officeEmail)
        return officeName || titleCaseWords(emailUser.replace(/[._-]+/g, " "));
      if (
        emailUser &&
        officeFirstName &&
        String(emailUser || "")
          .trim()
          .toLowerCase() === officeFirstName
      )
        return officeName || titleCaseWords(emailUser.replace(/[._-]+/g, " "));
      if (emailUser) return titleCaseWords(emailUser.replace(/[._-]+/g, " "));
    }

    return raw;
  }, []);

  const resolveTypeLabel = useCallback((event) => {
    const t = String(event?.type || "")
      .trim()
      .toUpperCase();
    if (t === "USER_NOTE_ADDED") return "Anotação do Advogado";
    if (t === "DOC_UPLOADED") return "Documento Anexado";
    if (t === "DOCUMENT_PUBLISHED") return "Documento Publicado";
    if (t === "PORTAL_UPLOAD_SUBMITTED") return "Envio do Cliente";
    if (t === "CLIENT_DOCUMENT_VIEWED") return "Leitura do Cliente";
    if (t === "PORTAL_UPLOAD_APPROVED") return "Arquivo Aprovado";
    if (t === "PORTAL_UPLOAD_REJECTED") return "Arquivo Devolvido";
    if (t === "CLIENT_CONSENT_ACCEPTED") return "Consentimento Aceito";
    if (t === "CLIENT_CONSENT_DECLINED") return "Consentimento Recusado";
    if (t === "PETITION_CONSENT_ACCEPTED") return "Petição Aprovada";
    if (t === "PETITION_CONSENT_DECLINED") return "Petição Recusada";
    if (t === "CLIENT_PORTAL_SESSION_STARTED") return "Portal Acessado";
    if (t === "CLIENT_PORTAL_PRONTUARIO_VIEWED") return "Prontuário Consultado";
    if (t === "CLIENT_PORTAL_PROCESSES_VIEWED") return "Processos Consultados";
    if (t === "CLIENT_PORTAL_FINANCE_VIEWED") return "Financeiro Consultado";
    if (t === "CLIENT_PORTAL_PROFILE_VIEWED") return "Perfil Consultado";
    if (t === "CLIENT_PORTAL_VAULT_VIEWED") return "Acervo Consultado";
    if (t === "PROCESS_CREATED") return "Processo Criado";
    if (t === "PROCESS_UPDATED") return "Processo Atualizado";
    if (t === "PROCESS_STATUS_UPDATED") return "Status do Processo";
    if (t === "PROCESS_MOVEMENT_UPDATED") return "Movimentação Processual";
    if (t === "PROCESS_MOVEMENT_ADDED") return "Andamento Adicionado";
    if (t === "PROCESS_MOVEMENT_EDITED") return "Andamento Atualizado";
    if (t === "FINANCIAL_INVOICE_CREATED") return "Fatura Disponibilizada";
    if (t === "FINANCIAL_ENTRY_POSTED") return "Lançamento Financeiro";
    if (t === "FINANCIAL_ENTRY_UPDATED") return "Financeiro Atualizado";
    if (t === "FINANCIAL_ENTRY_REMOVED") return "Financeiro Removido";
    if (t === "CLIENT_FINANCIAL_PIX_COPIED") return "Pix Copiado";
    if (t === "CLIENT_PORTAL_PAYMENT_SIMULATED") return "Pagamento Registrado";
    if (t === "DOCUMENT_CONSENT_REQUESTED") return "Consentimento Solicitado";
    return (
      String(event?.type || "").trim() ||
      (event?.category === "SYSTEM" ? "Sistema" : "Atividade")
    );
  }, []);

  const resolveTypeDetail = useCallback((event) => {
    const cat = String(event?.category || "")
      .trim()
      .toUpperCase();
    if (cat !== "SYSTEM") return "";
    const t = String(event?.type || "")
      .trim()
      .toUpperCase();
    if (!t) return "";
    if (t === "DOC_UPLOADED") return "";
    if (t === "DOCUMENT_PUBLISHED") return "";
    if (t === "USER_NOTE_ADDED") return "";
    if (t === "PETITION_CONSENT_ACCEPTED") return "";
    if (t === "PETITION_CONSENT_DECLINED") return "";
    if (t === "SYSTEM") return "";
    return String(event?.type || "").trim();
  }, []);

  const escapeHtml = useCallback((v) => {
    const s = String(v ?? "");
    return s
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }, []);

  const legacyTextToHtml = useCallback(
    (v) => {
      const s = String(v ?? "").trim();
      if (!s) return "";
      const safe = escapeHtml(s).replace(/\n/g, "<br />");
      return `<p>${safe}</p>`;
    },
    [escapeHtml],
  );

  const sanitizeHtml = useCallback(
    (html) => {
      const raw = String(html ?? "").trim();
      if (!raw) return "";
      if (
        typeof window === "undefined" ||
        typeof window.DOMParser === "undefined"
      ) {
        return legacyTextToHtml(raw);
      }

      const allowedTags = new Set([
        "P",
        "BR",
        "STRONG",
        "B",
        "EM",
        "I",
        "UL",
        "OL",
        "LI",
        "DIV",
        "SPAN",
      ]);
      const parser = new window.DOMParser();
      const doc = parser.parseFromString(`<div>${raw}</div>`, "text/html");
      const root = doc.body?.firstElementChild;
      if (!root) return "";

      const walk = (node) => {
        if (!node) return;

        const children = Array.from(node.childNodes || []);
        children.forEach((child) => {
          if (child.nodeType === 1) {
            const el = child;
            const tag = String(el.tagName || "").toUpperCase();

            if (!allowedTags.has(tag)) {
              const text = doc.createTextNode(el.textContent || "");
              el.replaceWith(text);
              return;
            }

            Array.from(el.attributes || []).forEach((a) => {
              try {
                el.removeAttribute(a.name);
              } catch (e) {
                void e;
              }
            });

            walk(el);
          } else if (child.nodeType === 8) {
            try {
              child.remove();
            } catch (e) {
              void e;
            }
          }
        });
      };

      walk(root);

      const out = String(root.innerHTML || "").trim();
      return out;
    },
    [legacyTextToHtml],
  );

  const getPlainTextFromHtml = useCallback((html) => {
    const raw = String(html ?? "").trim();
    if (!raw) return "";
    if (
      typeof window === "undefined" ||
      typeof window.DOMParser === "undefined"
    )
      return raw;
    try {
      const parser = new window.DOMParser();
      const doc = parser.parseFromString(`<div>${raw}</div>`, "text/html");
      const txt = String(doc?.body?.textContent || "").trim();
      return txt;
    } catch {
      return raw;
    }
  }, []);

  const resolvePayloadHtml = useCallback(
    (event) => {
      const payload =
        event?.payload && typeof event.payload === "object"
          ? event.payload
          : {};

      const rawHtml = String(payload?.html || "").trim();
      if (rawHtml) return rawHtml;

      const rawText = String(payload?.text || "").trim();
      if (!rawText) return "";

      const looksLikeHtml = rawText.includes("<") && rawText.includes(">");
      return looksLikeHtml ? rawText : legacyTextToHtml(rawText);
    },
    [legacyTextToHtml],
  );

  const editor = useEditor(
    {
      extensions: [
        StarterKit,
        Placeholder.configure({
          placeholder: "Registrar movimentação...",
        }),
      ],
      content: "",
      editorProps: {
        attributes: {
          class:
            "prose prose-sm max-w-none focus:outline-none min-h-[50px] px-3 py-2 text-sm text-slate-900",
        },
      },
      onFocus: () => setEditorFocused(true),
      onBlur: ({ editor }) => {
        try {
          const empty = editor?.isEmpty;
          if (empty) setEditorFocused(false);
        } catch {
          setEditorFocused(false);
        }
      },
      onUpdate: ({ editor }) => {
        try {
          setEditorHasContent(!editor?.isEmpty);
        } catch {
          setEditorHasContent(false);
        }
      },
    },
    [],
  );

  const loadFirstPage = useCallback(async () => {
    if (!notesRef) {
      setItems([]);
      setLastDocSnap(null);
      setHasMore(false);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const out = await fetchNotesPage(notesRef, PAGE_SIZE);
      setItems(out.items);
      setLastDocSnap(out.lastDoc);
      setHasMore(out.hasMore);
    } catch (e) {
      setItems([]);
      setLastDocSnap(null);
      setHasMore(false);
      setError(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  }, [notesRef]);

  const loadMore = useCallback(async () => {
    if (!notesRef) return;
    if (!hasMore) return;
    if (!lastDocSnap) return;

    setLoading(true);
    setError(null);
    try {
      const out = await fetchNotesPage(notesRef, PAGE_SIZE, lastDocSnap);

      setItems((prev) => {
        const base = Array.isArray(prev) ? prev : [];
        const merged = new Map();
        base.forEach((r) => merged.set(String(r?.id || ""), r));
        out.items.forEach((r) => merged.set(String(r?.id || ""), r));
        return Array.from(merged.values());
      });

      setLastDocSnap(out.lastDoc ?? lastDocSnap);
      setHasMore(out.hasMore);
    } catch (e) {
      setError(String(e?.message || e));
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [notesRef, hasMore, lastDocSnap]);

  useEffect(() => {
    try {
      editor?.commands?.setContent("");
    } catch (e) {
      void e;
    }
    setEditorFocused(false);
    setWorkspaceIdDraft("");
    setAuditWorkspaceId("__ALL__");
    setAuditType("ALL");
    setAuditSearch("");
    setSortKey("DT");
    setSortDir("desc");
    setAiSummary("");
    setAiSummaryError("");
    setIsSummarizing(false);
    loadFirstPage();
    loadWorkspaces();
  }, [id, loadFirstPage, editor, loadWorkspaces]);

  const handleAdd = useCallback(async () => {
    if (!editor) return;

    const rawHtml = String(editor.getHTML() || "").trim();
    const safeHtml = sanitizeHtml(rawHtml);
    const plain = getPlainTextFromHtml(safeHtml);
    if (!plain) return;
    if (!notesRef) return;

    const wid = String(workspaceIdDraft || "").trim();
    const resolvedLabel =
      wid && workspacesById.has(wid)
        ? String(workspacesById.get(wid) || "").trim()
        : wid
          ? wid
          : "Consulta Avulsa";

    setSaving(true);
    setError(null);
    try {
      await addClientNoteToRef(notesRef, {
        category: "USER",
        type: "USER_NOTE_ADDED",
        actor: { name: "Operador", isBot: false },
        payload: {
          html: safeHtml,
          text: plain,
          workspaceId: wid,
          workspaceLabel: resolvedLabel,
        },
        replies: [],
      });
      try {
        editor.commands.setContent("");
      } catch (e) {
        void e;
      }
      setEditorFocused(false);
      await loadFirstPage();
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setSaving(false);
    }
  }, [
    editor,
    notesRef,
    loadFirstPage,
    sanitizeHtml,
    getPlainTextFromHtml,
    workspaceIdDraft,
    workspacesById,
  ]);

  const togglePinned = useCallback(async () => {
    const ref = selectedEvent?.__ref;
    if (!ref) return;
    const nextPinned = !selectedEvent?.isPinned;

    try {
      await toggleNotePinned(ref, nextPinned);
      setSelectedEvent((prev) => {
        if (!prev) return prev;
        return { ...prev, isPinned: nextPinned };
      });
      setItems((prev) => {
        const base = Array.isArray(prev) ? prev : [];
        return base.map((it) =>
          String(it?.id || "") === String(selectedEvent?.id || "")
            ? { ...it, isPinned: nextPinned }
            : it,
        );
      });
    } catch (e) {
      void e;
    }
  }, [selectedEvent]);

  const addReply = useCallback(async () => {
    const ref = selectedEvent?.__ref;
    if (!ref) return;
    const text = String(replyDraft || "").trim();
    if (!text) return;

    const reply = {
      id: `${Date.now()}_${Math.random().toString(16).slice(2, 8)}`,
      text,
      by: "Rodrigo Demeterco",
      at: Date.now(),
    };

    setReplySaving(true);
    try {
      await addNoteReply(ref, reply);
      setReplyDraft("");
      setSelectedEvent((prev) => {
        if (!prev) return prev;
        const list = Array.isArray(prev?.replies) ? prev.replies : [];
        return { ...prev, replies: [...list, reply] };
      });
      setItems((prev) => {
        const base = Array.isArray(prev) ? prev : [];
        return base.map((it) => {
          if (String(it?.id || "") !== String(selectedEvent?.id || ""))
            return it;
          const list = Array.isArray(it?.replies) ? it.replies : [];
          return { ...it, replies: [...list, reply] };
        });
      });
    } catch (e) {
      void e;
    } finally {
      setReplySaving(false);
    }
  }, [replyDraft, selectedEvent]);

  const selectedDocumentMeta = useMemo(() => {
    const did = String(selectedEvent?.documentId || "").trim();
    const base = did ? documentMetaById?.[did] || {} : {};
    const pendingByEvent =
      String(selectedEvent?.typeUpper || "")
        .trim()
        .toUpperCase() === "DOCUMENT_CONSENT_REQUESTED";
    return {
      documentId: did,
      clientId: String(base?.clientId || id || "").trim(),
      title: String(
        base?.title || selectedEvent?.fileName || selectedEvent?.plain || "",
      ).trim(),
      fileUrl: String(
        base?.fileUrl || selectedEvent?.fileUrl || resolvedEventUrl || "",
      ).trim(),
      url: String(base?.url || "").trim(),
      downloadUrl: String(base?.downloadUrl || "").trim(),
      mirror_url: String(base?.mirror_url || "").trim(),
      storagePath: String(base?.storagePath || "").trim(),
      storage_path: String(base?.storage_path || "").trim(),
      storageEngine: base?.storageEngine || null,
      requiresConsent: base?.requiresConsent ?? pendingByEvent,
      consentStatus: String(
        base?.consentStatus || (pendingByEvent ? "pending_consent" : ""),
      ).trim(),
      status: String(base?.status || "").trim(),
    };
  }, [documentMetaById, id, resolvedEventUrl, selectedEvent]);

  const selectedActionState = useMemo(() => {
    return buildPublishedDocumentActionState({
      row: selectedEvent || {},
      meta: selectedDocumentMeta || {},
      resolvedUrl: resolvedEventUrl,
      consentSaving,
    });
  }, [consentSaving, resolvedEventUrl, selectedDocumentMeta, selectedEvent]);

  const selectedConsentAction = selectedActionState?.consentAction || {
    label: "Solicitar Consentimento",
    disabled: true,
    className: "border-slate-200 bg-slate-100 text-slate-400",
  };

  const handleRequestConsent = useCallback(async () => {
    const documentId = String(selectedDocumentMeta?.documentId || "").trim();
    if (!documentId || consentSaving || selectedConsentAction?.disabled) return;

    setConsentSaving(true);
    setConsentFeedback("");
    try {
      const result = await requestDocumentConsent({
        documentId,
        actorName: "Rodrigo Demeterco",
      });
      if (!result?.ok) {
        setConsentFeedback(
          String(
            result?.reason || "Não foi possível solicitar o consentimento.",
          ),
        );
        return;
      }

      const nextMeta = {
        documentId,
        clientId: String(
          result?.data?.clientId ||
            result?.data?.context?.primaryEntityId ||
            id ||
            "",
        ).trim(),
        title: String(
          result?.data?.title ||
            result?.data?.name ||
            result?.data?.fileName ||
            selectedDocumentMeta?.title ||
            "",
        ).trim(),
        fileUrl: String(
          result?.data?.fileUrl ||
            result?.data?.url ||
            result?.data?.downloadUrl ||
            selectedDocumentMeta?.fileUrl ||
            "",
        ).trim(),
        url: String(
          result?.data?.url || selectedDocumentMeta?.url || "",
        ).trim(),
        downloadUrl: String(
          result?.data?.downloadUrl || selectedDocumentMeta?.downloadUrl || "",
        ).trim(),
        mirror_url: String(
          result?.data?.mirror_url || selectedDocumentMeta?.mirror_url || "",
        ).trim(),
        storagePath: String(
          result?.data?.storagePath || selectedDocumentMeta?.storagePath || "",
        ).trim(),
        storage_path: String(
          result?.data?.storage_path ||
            result?.data?._metadata?.storage_path ||
            selectedDocumentMeta?.storage_path ||
            "",
        ).trim(),
        storageEngine:
          result?.data?.storageEngine ||
          selectedDocumentMeta?.storageEngine ||
          null,
        requiresConsent: Boolean(result?.data?.requiresConsent),
        consentStatus: String(result?.data?.consentStatus || "").trim(),
        status: String(result?.data?.status || "").trim(),
      };

      documentMetaRef.current = {
        ...(documentMetaRef.current || {}),
        [documentId]: nextMeta,
      };
      setDocumentMetaById(documentMetaRef.current || {});
      setConsentFeedback("Consentimento informado solicitado com sucesso.");
    } catch (e) {
      setConsentFeedback(
        String(e?.message || e || "Falha ao solicitar consentimento."),
      );
    } finally {
      setConsentSaving(false);
    }
  }, [
    consentSaving,
    id,
    selectedConsentAction?.disabled,
    selectedDocumentMeta,
  ]);

  useEffect(() => {
    const ids = new Set();
    (Array.isArray(items) ? items : []).forEach((row) => {
      const normalized = normalizeEvent(row);
      const did = String(normalized?.payload?.documentId || "").trim();
      if (!did) return;
      if (documentMetaRef.current && documentMetaRef.current[did]) return;
      ids.add(did);
    });
    if (ids.size === 0) return;
    if (documentLoadBusyRef.current) return;

    documentLoadBusyRef.current = true;

    (async () => {
      try {
        const list = Array.from(ids);
        const metaMap = await fetchDocumentsMetaBatch(list, id);
        documentMetaRef.current = { ...documentMetaRef.current, ...metaMap };

        setDocumentMetaById(documentMetaRef.current || {});
      } finally {
        documentLoadBusyRef.current = false;
      }
    })();
  }, [id, items, normalizeEvent]);

  const gridRows = useMemo(() => {
    const base = (Array.isArray(items) ? items : []).map((row) => {
      const rid = String(row?.id || "").trim();
      const dt = row?.createdAt?.toDate ? row.createdAt.toDate() : null;
      const when = dt ? formatGridDt(dt) : "";

      const event = normalizeEvent(row);
      const eventTypeUpper = String(event?.type || "")
        .trim()
        .toUpperCase();
      const isSystem = event?.category === "SYSTEM";
      const isDocUploaded = eventTypeUpper === "DOC_UPLOADED";

      const rawActorNameNotes = String(event?.actor?.name || "").trim();
      const who =
        isDocUploaded && rawActorNameNotes.toLowerCase() === "sistema"
          ? humanizeActorName("Operador", "SYSTEM") || "—"
          : humanizeActorName(rawActorNameNotes, event?.category) || "—";
      const isDocumentPublished = eventTypeUpper === "DOCUMENT_PUBLISHED";
      const isDocumentConsentRequested =
        eventTypeUpper === "DOCUMENT_CONSENT_REQUESTED";

      const payloadText = String(event?.payload?.text || "").trim();
      const payloadFileName = String(event?.payload?.fileName || "").trim();
      const payloadFileUrl = String(
        event?.payload?.fileUrl ||
          event?.payload?.downloadUrl ||
          event?.payload?.url ||
          "",
      ).trim();
      const payloadDocumentId = String(event?.payload?.documentId || "").trim();
      const payloadWorkspaceId = String(
        event?.payload?.workspaceId || "",
      ).trim();
      const payloadWorkspaceLabel = String(
        event?.payload?.workspaceLabel || "",
      ).trim();
      const resolvedWorkspaceLabel =
        normalizeWorkspaceLabel(payloadWorkspaceLabel) ||
        (payloadWorkspaceId
          ? String(workspacesById.get(payloadWorkspaceId) || payloadWorkspaceId)
          : isDocUploaded
            ? "Vinculação de Documento"
            : "Consulta Avulsa");
      const documentMeta =
        payloadDocumentId && documentMetaRef.current
          ? documentMetaRef.current[payloadDocumentId]
          : null;
      const canonicalTitle = String(documentMeta?.title || "").trim();
      const canonicalFileUrl = String(documentMeta?.fileUrl || "").trim();
      const publicationSeriesId = String(
        event?.payload?.publicationSeriesId ||
          documentMeta?.publicationSeriesId ||
          payloadDocumentId ||
          "",
      ).trim();
      const publicationVersion = Math.max(
        0,
        Number(
          event?.payload?.publicationVersion ||
            documentMeta?.publicationVersion ||
            0,
        ) || 0,
      );
      const moduleName = String(
        event?.payload?.module || documentMeta?.module || "",
      ).trim();

      const typeLabel = (() => {
        if (isDocumentConsentRequested) return "Consentimento Solicitado";
        if (isDocumentPublished) return "Documento Publicado";
        if (isDocUploaded) return "Documento";
        if (!isSystem) return "Anotação";
        return "Sistema";
      })();

      const treatAsSystemBody =
        isSystem && !(isDocumentPublished || isDocumentConsentRequested);
      const safeUserHtml = treatAsSystemBody
        ? ""
        : sanitizeHtml(resolvePayloadHtml(event) || payloadText);
      const plain = treatAsSystemBody
        ? String(canonicalTitle || payloadFileName || payloadText || "").trim()
        : String(getPlainTextFromHtml(safeUserHtml) || "").trim();

      const replies = Array.isArray(row?.replies) ? row.replies : [];
      const repliesCount = replies.length;

      const searchBlob = String(
        [
          when,
          typeLabel,
          resolveTypeLabel(event),
          who,
          payloadWorkspaceId,
          resolvedWorkspaceLabel,
          payloadFileName,
          plain,
        ]
          .filter(Boolean)
          .join(" | "),
      )
        .toLowerCase()
        .trim();

      return {
        id: rid,
        __ref: row?.__ref,
        dt,
        when,
        event,
        who,
        isSystem,
        isDocUploaded,
        isDocumentPublished,
        isDocumentConsentRequested,
        typeLabel,
        typeUpper: eventTypeUpper,
        isPinned: Boolean(row?.isPinned),
        replies,
        repliesCount,
        workspaceId: payloadWorkspaceId,
        workspaceLabel: resolvedWorkspaceLabel,
        fileName: canonicalTitle || payloadFileName,
        fileUrl: payloadFileUrl || canonicalFileUrl,
        documentId: payloadDocumentId,
        module: moduleName,
        publicationSeriesId,
        publicationVersion,
        storageEngine: documentMeta?.storageEngine || null,
        storagePath: String(documentMeta?.storagePath || "").trim(),
        storage_path: String(documentMeta?.storage_path || "").trim(),
        safeUserHtml,
        plain,
        searchBlob,
      };
    });

    const search = String(auditSearch || "")
      .toLowerCase()
      .trim();

    let out = base;

    if (auditWorkspaceId !== "__ALL__") {
      const wid = String(auditWorkspaceId || "");
      if (wid === "")
        out = out.filter((r) => !String(r?.workspaceId || "").trim());
      else out = out.filter((r) => String(r?.workspaceId || "").trim() === wid);
    }

    if (auditType !== "ALL") {
      out = out.filter((r) => {
        if (auditType === "DOCS")
          return (
            r?.typeUpper === "DOC_UPLOADED" ||
            r?.typeUpper === "DOCUMENT_PUBLISHED"
          );
        if (auditType === "NOTES") return r?.typeUpper === "USER_NOTE_ADDED";
        if (auditType === "SYSTEM")
          return (
            r?.isSystem &&
            r?.typeUpper !== "DOC_UPLOADED" &&
            r?.typeUpper !== "DOCUMENT_PUBLISHED"
          );
        if (auditType === "PETITIONS")
          return (
            r?.typeUpper === "PETITION_CONSENT_ACCEPTED" ||
            r?.typeUpper === "PETITION_CONSENT_DECLINED" ||
            r?.typeUpper === "PETITION_PUBLISHED"
          );
        return true;
      });
    }

    if (search) {
      out = out.filter((r) => String(r?.searchBlob || "").includes(search));
    }

    const dir = sortDir === "asc" ? 1 : -1;
    const key = String(sortKey || "DT");

    const safeLower = (v) =>
      String(v || "")
        .toLowerCase()
        .trim();

    const sorter = (a, b) => {
      if (key === "DT") {
        const av = a?.dt instanceof Date ? a.dt.getTime() : 0;
        const bv = b?.dt instanceof Date ? b.dt.getTime() : 0;
        return (av - bv) * dir;
      }
      if (key === "TYPE")
        return (
          safeLower(a?.typeLabel).localeCompare(safeLower(b?.typeLabel)) * dir
        );
      if (key === "AUTHOR")
        return safeLower(a?.who).localeCompare(safeLower(b?.who)) * dir;
      if (key === "WORKSPACE")
        return (
          safeLower(a?.workspaceLabel).localeCompare(
            safeLower(b?.workspaceLabel),
          ) * dir
        );
      return 0;
    };

    const pinned = out.filter((r) => r?.isPinned).sort(sorter);
    const rest = out.filter((r) => !r?.isPinned).sort(sorter);
    out = [...pinned, ...rest];

    return out;
  }, [
    items,
    auditSearch,
    auditType,
    auditWorkspaceId,
    documentMetaById,
    formatGridDt,
    getPlainTextFromHtml,
    humanizeActorName,
    normalizeEvent,
    resolvePayloadHtml,
    resolveTypeLabel,
    sanitizeHtml,
    sortDir,
    sortKey,
    workspacesById,
  ]);

  const latestPublicationVersionBySeries = useMemo(() => {
    return buildLatestPublicationVersionBySeries(
      Object.values(documentMetaById || {}),
      gridRows,
    );
  }, [documentMetaById, gridRows]);

  const canEditSelectedEvent = useMemo(() => {
    return Boolean(
      resolvePublishedDocumentEditTarget(selectedEvent) &&
      String(selectedEvent?.documentId || "").trim(),
    );
  }, [selectedEvent]);

  const handleEditPublishedDocument = useCallback(
    (row) => {
      const enrichedRow = {
        ...(row || {}),
        documentId: String(
          selectedDocumentMeta?.documentId || row?.documentId || row?.id || "",
        ).trim(),
        publicationSeriesId: String(
          selectedDocumentMeta?.publicationSeriesId ||
            row?.publicationSeriesId ||
            row?.documentId ||
            row?.id ||
            "",
        ).trim(),
        publicationVersion: Math.max(
          0,
          Number(
            selectedDocumentMeta?.publicationVersion ||
              row?.publicationVersion ||
              0,
          ) || 0,
        ),
        title: String(
          selectedDocumentMeta?.title ||
            row?.title ||
            row?.fileName ||
            row?.name ||
            "",
        ).trim(),
        fileUrl: String(
          selectedDocumentMeta?.fileUrl || row?.fileUrl || "",
        ).trim(),
        url: String(selectedDocumentMeta?.url || row?.url || "").trim(),
        downloadUrl: String(
          selectedDocumentMeta?.downloadUrl || row?.downloadUrl || "",
        ).trim(),
        mirror_url: String(
          selectedDocumentMeta?.mirror_url || row?.mirror_url || "",
        ).trim(),
        storagePath: String(
          selectedDocumentMeta?.storagePath || row?.storagePath || "",
        ).trim(),
        storage_path: String(
          selectedDocumentMeta?.storage_path || row?.storage_path || "",
        ).trim(),
      };
      const request = queuePublishedDocumentEdit(enrichedRow);
      if (!request) return;
      setFlyoutOpen(false);
    },
    [selectedDocumentMeta],
  );

  const summarySourceRows = useMemo(() => {
    const ranked = [...gridRows]
      .filter((row) => String(row?.plain || "").trim())
      .sort((a, b) => {
        const av = a?.dt instanceof Date ? a.dt.getTime() : 0;
        const bv = b?.dt instanceof Date ? b.dt.getTime() : 0;
        return bv - av;
      })
      .slice(0, AI_SUMMARY_MAX_NOTES)
      .reverse();

    return ranked;
  }, [gridRows]);

  const handleGenerateSummary = useCallback(async () => {
    if (isSummarizing) return;

    const notesPayload = summarySourceRows
      .map((row) => {
        const when = String(row?.when || "").trim();
        const typeLabel = String(row?.typeLabel || "").trim();
        const who = String(row?.who || "").trim();
        const plain = String(row?.plain || "")
          .replace(/\s+/g, " ")
          .trim();
        if (!plain) return "";
        return [when, typeLabel, who, plain].filter(Boolean).join(" | ");
      })
      .filter(Boolean)
      .join("\n");

    if (!notesPayload) {
      const reason =
        "Não há notas recentes com conteúdo textual suficiente para gerar o sumário executivo.";
      setAiSummary("");
      setAiSummaryError(reason);
      return;
    }

    setIsSummarizing(true);
    setAiSummary("");
    setAiSummaryError("");

    try {
      const result = await generateTextWithGemini(
        AI_SUMMARY_SYSTEM_PROMPT,
        notesPayload,
        { tier: "pro" },
      );
      const clean = String(result || "")
        .replace(/\s+/g, " ")
        .trim();
      if (!clean) throw new Error("A IA retornou um sumário vazio.");
      setAiSummary(clean);
    } catch (e) {
      setAiSummary("");
      setAiSummaryError(
        String(e?.message || e || "Falha ao gerar sumário executivo."),
      );
      try {
        alert(
          "Não foi possível gerar o sumário executivo com IA. Verifique a conectividade e a configuração do Gemini.",
        );
      } catch (alertErr) {
        void alertErr;
      }
    } finally {
      setIsSummarizing(false);
    }
  }, [isSummarizing, summarySourceRows]);

  const handleCopySummary = useCallback(async () => {
    const payload = String(aiSummary || "").trim();
    if (!payload) return;

    try {
      await navigator.clipboard.writeText(payload);
    } catch (e) {
      setAiSummaryError(
        "Não foi possível copiar automaticamente. Copie o texto manualmente.",
      );
      try {
        alert(
          "Não foi possível copiar automaticamente para a área de transferência.",
        );
      } catch (alertErr) {
        void alertErr;
      }
    }
  }, [aiSummary]);

  const openEventDocument = useCallback(
    async (row) => {
      const payload =
        row?.event?.payload && typeof row.event.payload === "object"
          ? row.event.payload
          : {};
      const directPayloadUrl = String(row?.fileUrl || "").trim();
      const resolvedLegacyUrl = String(resolvedEventUrl || "").trim();
      const documentId = String(row?.documentId || "").trim();
      const title =
        String(
          row?.fileName ||
            row?.plain ||
            row?.typeLabel ||
            "Documento Publicado",
        ).trim() || "Documento Publicado";
      const when = String(row?.when || "").trim() || "";
      const who = String(row?.who || "").trim() || "";
      const eventTypeUpper = String(row?.typeUpper || "")
        .trim()
        .toUpperCase();

      let targetUrl = String(
        directPayloadUrl || resolvedLegacyUrl || "",
      ).trim();
      if (!targetUrl) {
        try {
          targetUrl = String(
            await resolveDocumentUrl({
              storageEngine: row?.storageEngine || null,
              storagePath: String(row?.storagePath || "").trim(),
              storage_path: String(row?.storage_path || "").trim(),
              fileUrl: directPayloadUrl,
              url: String(payload?.url || "").trim(),
              downloadUrl: String(payload?.downloadUrl || "").trim(),
              mirror_url: String(payload?.mirror_url || "").trim(),
            }),
          ).trim();
        } catch (e) {
          void e;
        }
      }

      if (
        eventTypeUpper === "DOCUMENT_PUBLISHED" &&
        (targetUrl || documentId)
      ) {
        const viewerUrl = `/view-published?${new URLSearchParams({
          ...(targetUrl ? { url: targetUrl } : {}),
          ...(documentId ? { documentId } : {}),
          ...(title ? { title } : {}),
          ...(when ? { when } : {}),
          ...(who ? { who } : {}),
        }).toString()}`;

        try {
          window.open(viewerUrl, "_blank", "noopener,noreferrer");
        } catch (e) {
          try {
            window.open(viewerUrl, "_blank");
          } catch (e2) {
            void e;
            void e2;
          }
        }
        return;
      }

      if (targetUrl) {
        try {
          window.open(targetUrl, "_blank", "noopener,noreferrer");
        } catch (e) {
          try {
            window.open(targetUrl, "_blank");
          } catch (e2) {
            void e;
            void e2;
          }
        }
        return;
      }

      let rawHtml = String(payload?.html || "").trim();
      const rawText = String(payload?.text || "").trim();
      const fallbackTitle =
        String(
          row?.fileName ||
            row?.plain ||
            row?.typeLabel ||
            "Documento Publicado",
        ).trim() || "Documento Publicado";
      const fallbackWhen =
        String(row?.when || "").trim() || "Data não informada";
      const fallbackWho =
        String(row?.who || "").trim() || "Autor não informado";
      const workspace =
        String(row?.workspaceLabel || "").trim() || "Consulta Avulsa";
      const docId = String(row?.documentId || "").trim();

      const bodyHtml = rawHtml
        ? rawHtml
        : rawText
          ? `<p>${escapeHtml(rawText)}</p>`
          : "<p>Não foi possível recuperar o HTML original deste documento legado. Este registro foi reconstruído a partir dos metadados disponíveis no prontuário.</p>";

      const fallbackHtml = `<!doctype html>
<html lang="pt-BR">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${escapeHtml(fallbackTitle)}</title>
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&display=swap">
  <style>
    html, body { margin: 0; padding: 0; background: #f3f4f6; color: #20243B; font-family: 'Roboto', sans-serif; -webkit-font-smoothing: antialiased; }
    .shell { max-width: 1120px; margin: 0 auto; padding: 24px; }
    .banner { margin-bottom: 16px; border: 1px solid #dbe2ea; background: #fff; border-radius: 16px; padding: 16px 18px; }
    .banner h1 { margin: 0 0 8px 0; font-size: 18px; font-weight: 800; }
    .banner p { margin: 4px 0; font-size: 12px; line-height: 1.6; }
    .paper { background: #fff; border: 1px solid #e5e7eb; border-radius: 16px; padding: 24px; box-shadow: 0 10px 25px rgba(15, 23, 42, 0.06); }
    .paper .prose { font-size: 14px; line-height: 1.7; }
  </style>
</head>
<body>
  <div class="shell">
    <div class="banner">
      <h1>${escapeHtml(fallbackTitle)}</h1>
      <p>Registro legado reconstruído em nova guia a partir do prontuário do cliente, publicado por ${escapeHtml(fallbackWho)} em ${escapeHtml(fallbackWhen)}.</p>
      <p>Processo: ${escapeHtml(workspace)}${docId ? ` • Documento ID: ${escapeHtml(docId)}` : ""}</p>
    </div>
    <div class="paper">
      <div class="prose">${bodyHtml}</div>
    </div>
  </div>
</body>
</html>`;

      try {
        const blob = new Blob([fallbackHtml], {
          type: "text/html;charset=utf-8",
        });
        const blobUrl = URL.createObjectURL(blob);
        const win = window.open(blobUrl, "_blank", "noopener,noreferrer");
        if (win) {
          setTimeout(() => {
            try {
              URL.revokeObjectURL(blobUrl);
            } catch (e) {
              void e;
            }
          }, 60000);
          return;
        }
      } catch (e) {
        void e;
      }
    },
    [escapeHtml, resolvedEventUrl],
  );

  if (!id) {
    return (
      <div className="text-xs text-slate-500">
        Selecione um cliente para ver o prontuário.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 overflow-x-hidden">
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="text-xs font-extrabold uppercase tracking-widest text-slate-600">
          Movimentação
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-2">
          <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500">
            Vincular a:
          </div>
          <select
            value={workspaceIdDraft}
            onChange={(e) => setWorkspaceIdDraft(String(e.target.value || ""))}
            className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-[11px] font-extrabold uppercase tracking-widest text-slate-700 hover:bg-slate-50"
            disabled={workspacesLoading}
            title="Vincular esta nota a um Processo/Caderno"
          >
            <option value="">Consulta Avulsa</option>
            {(Array.isArray(workspaces) ? workspaces : []).map((w) => {
              const wid = String(w?.id || "").trim();
              if (!wid) return null;
              const pieceType =
                String(w?.pieceType || w?.title || "Processo").trim() ||
                "Processo";
              const label = pieceType;
              return (
                <option key={wid} value={wid}>
                  {label}
                </option>
              );
            })}
          </select>
        </div>

        <div
          className={
            "mt-3 rounded-lg border border-slate-200 bg-white " +
            (editorFocused ? "shadow-md" : "shadow-none")
          }
          onMouseDown={() => {
            try {
              editor?.commands?.focus();
            } catch (e) {
              void e;
            }
          }}
        >
          <EditorContent editor={editor} />
        </div>

        {editorFocused ? (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleBold().run()}
              className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-[11px] font-extrabold uppercase tracking-widest text-slate-700 hover:bg-slate-50"
              title="Negrito"
            >
              Negrito
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleItalic().run()}
              className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-[11px] font-extrabold uppercase tracking-widest text-slate-700 hover:bg-slate-50"
              title="Itálico"
            >
              Itálico
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-[11px] font-extrabold uppercase tracking-widest text-slate-700 hover:bg-slate-50"
              title="Lista"
            >
              Lista
            </button>
          </div>
        ) : null}

        <div className="mt-2 flex items-center justify-end">
          <button
            type="button"
            disabled={saving || !editor || !editorHasContent}
            onClick={handleAdd}
            className={
              "px-4 py-2 rounded-lg text-xs font-extrabold uppercase tracking-widest border " +
              (saving || !editor || !editorHasContent
                ? "border-slate-200 bg-slate-100 text-slate-400"
                : "border-blue-300 bg-blue-50 text-blue-800 hover:bg-blue-100")
            }
          >
            {saving ? "Salvando..." : "Adicionar Nota"}
          </button>
        </div>
      </div>

      {error ? (
        <div className="p-3 rounded-lg border border-red-200 bg-red-50 text-xs text-red-700 whitespace-pre-wrap">
          {error}
        </div>
      ) : null}

      {loading ? (
        <div className="text-xs text-slate-500">Carregando...</div>
      ) : null}

      {!loading && !error && items.length === 0 ? (
        <div className="text-xs text-slate-500">Nenhuma anotação ainda.</div>
      ) : null}

      <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-[11px] font-extrabold uppercase tracking-widest text-slate-600">
            Auditoria
          </div>
          <button
            type="button"
            onClick={handleGenerateSummary}
            disabled={isSummarizing || summarySourceRows.length === 0}
            className="inline-flex items-center gap-2 rounded-lg border border-[#C5A059]/45 bg-white px-3 py-2 text-sm font-bold text-[#7A5C1B] hover:bg-amber-50 disabled:opacity-50"
          >
            {isSummarizing ? (
              <i className="fas fa-spinner fa-spin" />
            ) : (
              <Sparkles size={14} />
            )}
            {isSummarizing
              ? "Analisando histórico..."
              : "✨ Gerar Sumário com IA"}
          </button>
        </div>

        {aiSummary ? (
          <div
            className="mt-3 rounded-xl border bg-[rgba(197,160,89,0.08)] p-4 shadow-sm"
            style={{ borderColor: "#C5A059" }}
          >
            <div className="text-[10px] font-extrabold uppercase tracking-widest text-[#7A5C1B]">
              Sumário Executivo
            </div>
            <div className="mt-2 text-sm leading-7 text-slate-800 text-justify">
              {aiSummary}
            </div>
            <div className="mt-3 flex justify-end">
              <button
                type="button"
                onClick={handleCopySummary}
                className="inline-flex items-center gap-2 rounded-lg border border-[#C5A059]/45 bg-white px-3 py-2 text-xs font-extrabold uppercase tracking-widest text-[#7A5C1B] hover:bg-white"
              >
                <Copy size={14} />
                📋 Copiar para WhatsApp
              </button>
            </div>
          </div>
        ) : null}

        {aiSummaryError ? (
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-bold text-amber-900">
            {aiSummaryError}
          </div>
        ) : null}

        <div className="mt-2 grid grid-cols-1 md:grid-cols-[minmax(0,220px)_minmax(0,180px)_minmax(0,1fr)] gap-2 items-center">
          <div>
            <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500">
              Processo
            </div>
            <select
              value={auditWorkspaceId}
              onChange={(e) =>
                setAuditWorkspaceId(String(e.target.value || "__ALL__"))
              }
              className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-[11px] font-extrabold uppercase tracking-widest text-slate-700 hover:bg-slate-50"
              title="Filtrar por processo vinculado"
            >
              <option value="__ALL__">Todos</option>
              <option value="">Consulta Avulsa</option>
              {(Array.isArray(workspaces) ? workspaces : []).map((w) => {
                const wid = String(w?.id || "").trim();
                if (!wid) return null;
                const pieceType =
                  String(w?.pieceType || w?.title || "Processo").trim() ||
                  "Processo";
                const label = pieceType;
                return (
                  <option key={wid} value={wid}>
                    {label}
                  </option>
                );
              })}
            </select>
          </div>

          <div>
            <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500">
              Tipo
            </div>
            <select
              value={auditType}
              onChange={(e) => setAuditType(String(e.target.value || "ALL"))}
              className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-[11px] font-extrabold uppercase tracking-widest text-slate-700 hover:bg-slate-50"
              title="Filtrar por tipo"
            >
              <option value="ALL">Todos</option>
              <option value="DOCS">Só Documentos</option>
              <option value="NOTES">Só Notas</option>
              <option value="SYSTEM">Só Sistema</option>
              <option value="PETITIONS">Petições</option>
            </select>
          </div>

          <div>
            <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500">
              Busca
            </div>
            <div className="mt-1 relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                <Search size={14} />
              </div>
              <input
                value={auditSearch}
                onChange={(e) => setAuditSearch(String(e.target.value || ""))}
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 bg-white text-xs text-slate-700 placeholder:text-slate-400"
                placeholder="Buscar em autor, processo, conteúdo, arquivo..."
              />
            </div>
          </div>
        </div>
      </div>

      {!loading && !error && gridRows.length > 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-y-auto overflow-x-hidden max-h-[60vh]">
          <table className="w-full table-fixed text-sm">
            <thead className="sticky top-0 z-10 bg-slate-100 border-b border-slate-200">
              <tr className="text-[10px] font-extrabold uppercase tracking-widest text-slate-600">
                <th
                  className="w-[200px] px-3 py-2 text-left cursor-pointer select-none"
                  onClick={() => {
                    if (sortKey === "WORKSPACE")
                      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
                    else {
                      setSortKey("WORKSPACE");
                      setSortDir("asc");
                    }
                  }}
                >
                  Processo
                </th>

                <th
                  className="w-[140px] px-3 py-2 text-left cursor-pointer select-none"
                  onClick={() => {
                    if (sortKey === "DT")
                      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
                    else {
                      setSortKey("DT");
                      setSortDir("desc");
                    }
                  }}
                >
                  Data/Hora
                </th>

                <th
                  className="w-[120px] px-3 py-2 text-left cursor-pointer select-none"
                  onClick={() => {
                    if (sortKey === "TYPE")
                      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
                    else {
                      setSortKey("TYPE");
                      setSortDir("asc");
                    }
                  }}
                >
                  Tipo
                </th>

                <th
                  className="w-[140px] px-3 py-2 text-left cursor-pointer select-none"
                  onClick={() => {
                    if (sortKey === "AUTHOR")
                      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
                    else {
                      setSortKey("AUTHOR");
                      setSortDir("asc");
                    }
                  }}
                >
                  Autor
                </th>

                <th className="px-3 py-2 text-left">Conteúdo</th>
                <th className="w-[80px] px-3 py-2 text-right">Ações</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-200">
              {gridRows.map((r) => {
                const rowId = String(r?.id || "");
                const versionState = buildPublicationVersionState({
                  row: r,
                  latestPublicationVersionBySeries,
                });
                const isCurrentVersion = Boolean(
                  versionState?.isCurrentVersion,
                );
                const isHistoricalVersion = Boolean(
                  versionState?.isHistoricalVersion,
                );

                const rowBg = isHistoricalVersion
                  ? "bg-slate-50 opacity-75 grayscale"
                  : isCurrentVersion
                    ? "bg-emerald-50/40"
                    : r?.isPinned
                      ? "bg-blue-50/30"
                      : r?.isSystem
                        ? "bg-slate-50"
                        : "bg-white";
                const rowClass = `${rowBg} cursor-pointer hover:bg-slate-100`;

                const systemBody = String(r?.fileName || r?.plain || "").trim();
                const typeDetail = resolveTypeDetail(r?.event);
                const versionBadge = String(
                  versionState?.versionBadge || "",
                ).trim();

                return (
                  <tr
                    key={rowId}
                    className={rowClass}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      setSelectedEvent(r);
                      setFlyoutOpen(true);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setSelectedEvent(r);
                        setFlyoutOpen(true);
                      }
                    }}
                  >
                    <td
                      className={`px-3 py-2 align-top ${isCurrentVersion ? "border-l-4 border-emerald-500" : isHistoricalVersion ? "border-l-4 border-slate-300" : ""}`}
                    >
                      <div
                        className="text-sm text-slate-600 truncate"
                        title={normalizeWorkspaceLabel(r?.workspaceLabel)}
                      >
                        {normalizeWorkspaceLabel(r?.workspaceLabel) ||
                          "Consulta Avulsa"}
                      </div>
                    </td>

                    <td className="px-3 py-2 align-top whitespace-nowrap font-mono text-sm text-slate-600">
                      {r?.when || "—"}
                    </td>

                    <td className="px-3 py-2 align-top">
                      <div className="flex flex-col gap-1.5">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-700">
                            {r?.typeLabel}
                          </span>
                          {versionBadge ? (
                            <span
                              className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest ${isCurrentVersion ? "bg-emerald-100 text-emerald-800" : "bg-gray-100 text-gray-500"}`}
                            >
                              {versionBadge}
                            </span>
                          ) : null}
                        </div>
                        {typeDetail ? (
                          <div
                            className="text-sm text-slate-600 truncate"
                            title={typeDetail}
                          >
                            {typeDetail}
                          </div>
                        ) : null}
                      </div>
                    </td>

                    <td className="px-3 py-2 align-top text-sm text-slate-600">
                      <div className="truncate" title={r?.who}>
                        {r?.who || "—"}
                      </div>
                    </td>

                    <td className="px-3 py-2 align-top">
                      {r?.isSystem ? (
                        systemBody ? (
                          <div
                            className="truncate text-sm text-slate-700"
                            title={systemBody}
                          >
                            {systemBody}
                          </div>
                        ) : (
                          <div className="text-sm text-slate-500">—</div>
                        )
                      ) : r?.safeUserHtml ? (
                        <div
                          className="text-sm text-slate-700 line-clamp-2 max-w-[520px]"
                          dangerouslySetInnerHTML={{ __html: r.safeUserHtml }}
                        />
                      ) : r?.plain ? (
                        <div
                          className="truncate text-sm text-slate-700"
                          title={r.plain}
                        >
                          {r.plain}
                        </div>
                      ) : (
                        <div className="text-sm text-slate-500">—</div>
                      )}
                    </td>

                    <td className="px-3 py-2 align-top text-right">
                      <div className="inline-flex items-center justify-end gap-2">
                        {r?.isPinned ? (
                          <span
                            className="inline-flex items-center text-blue-700"
                            title="Fixado"
                          >
                            <Pin size={14} />
                          </span>
                        ) : null}
                        {Number(r?.repliesCount || 0) > 0 ? (
                          <span
                            className="inline-flex items-center gap-1 text-slate-500"
                            title="Respostas"
                          >
                            <MessageSquare size={14} />
                            <span className="text-sm text-slate-600">
                              {r.repliesCount}
                            </span>
                          </span>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}

      {hasMore ? (
        <div className="pt-2 flex justify-center">
          <button
            type="button"
            disabled={loading}
            onClick={loadMore}
            className="px-4 py-2 rounded-lg text-xs font-extrabold uppercase tracking-widest border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
          >
            Carregar mais
          </button>
        </div>
      ) : null}

      {selectedEvent ? (
        <div className="fixed inset-0 z-50">
          <div
            className={
              "absolute inset-0 bg-black/20 " +
              (flyoutOpen ? "opacity-100" : "opacity-0 pointer-events-none")
            }
            onClick={() => setFlyoutOpen(false)}
          />

          <div
            className={
              "fixed inset-y-0 right-0 w-full max-w-lg bg-white shadow-2xl z-50 transform " +
              (flyoutOpen ? "translate-x-0" : "translate-x-full")
            }
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-full flex flex-col">
              <div className="p-4 border-b border-slate-200">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-xs font-extrabold uppercase tracking-widest text-slate-600">
                      {selectedEvent?.typeLabel || "Evento"}
                    </div>
                    <div className="mt-1 text-[11px] text-slate-500 whitespace-pre-wrap">
                      {[
                        selectedEvent?.when
                          ? `Quando: ${selectedEvent.when}`
                          : "",
                        selectedEvent?.who ? `Autor: ${selectedEvent.who}` : "",
                        selectedEvent?.workspaceLabel
                          ? `Processo: ${selectedEvent.workspaceLabel}`
                          : "",
                      ]
                        .filter(Boolean)
                        .join(" • ")}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setFlyoutOpen(false)}
                      className="px-3 py-2 rounded-lg text-xs font-extrabold uppercase tracking-widest border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                      aria-label="Fechar"
                      title="Fechar"
                    >
                      X
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto">
                {selectedEvent?.isSystem ? (
                  <div className="p-6">
                    <div className="prose prose-sm max-w-none">
                      <div className="whitespace-pre-wrap text-sm text-slate-800">
                        {String(
                          selectedEvent?.fileName || selectedEvent?.plain || "",
                        ).trim() || "—"}
                      </div>
                    </div>

                    <div className="mt-6">
                      <div className="text-[11px] font-extrabold uppercase tracking-widest text-slate-600">
                        Respostas
                      </div>
                      <div className="mt-2">
                        {(Array.isArray(selectedEvent?.replies)
                          ? selectedEvent.replies
                          : []
                        ).map((rep) => {
                          const rid = String(rep?.id || "");
                          const by = String(rep?.by || "").trim() || "—";
                          const at = rep?.at
                            ? new Date(rep.at).toLocaleString("pt-BR")
                            : "";
                          const text = String(rep?.text || "").trim();
                          if (!rid && !text) return null;
                          return (
                            <div
                              key={rid || at || text}
                              className="bg-slate-50 border border-slate-100 rounded-lg p-3 mt-2"
                            >
                              <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500">
                                {by}
                                {at ? ` • ${at}` : ""}
                              </div>
                              <div className="mt-1 text-[13px] text-slate-800 whitespace-pre-wrap">
                                {text || "—"}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ) : selectedEvent?.safeUserHtml ? (
                  <div className="p-6">
                    <div
                      className="prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{
                        __html: selectedEvent.safeUserHtml,
                      }}
                    />

                    <div className="mt-6">
                      <div className="text-[11px] font-extrabold uppercase tracking-widest text-slate-600">
                        Respostas
                      </div>
                      <div className="mt-2">
                        {(Array.isArray(selectedEvent?.replies)
                          ? selectedEvent.replies
                          : []
                        ).map((rep) => {
                          const rid = String(rep?.id || "");
                          const by = String(rep?.by || "").trim() || "—";
                          const at = rep?.at
                            ? new Date(rep.at).toLocaleString("pt-BR")
                            : "";
                          const text = String(rep?.text || "").trim();
                          if (!rid && !text) return null;
                          return (
                            <div
                              key={rid || at || text}
                              className="bg-slate-50 border border-slate-100 rounded-lg p-3 mt-2"
                            >
                              <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500">
                                {by}
                                {at ? ` • ${at}` : ""}
                              </div>
                              <div className="mt-1 text-[13px] text-slate-800 whitespace-pre-wrap">
                                {text || "—"}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="p-6 text-sm text-slate-500">—</div>
                )}
              </div>

              <div className="sticky bottom-0 border-t border-slate-200 bg-white/95 backdrop-blur p-4">
                <div className="flex flex-col gap-2 w-full">
                  {selectedActionState?.canEditDocument ? (
                    <button
                      type="button"
                      onClick={() => handleEditPublishedDocument(selectedEvent)}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold border border-blue-200 bg-blue-50 text-blue-800 hover:bg-blue-100"
                    >
                      ✏️ Editar Documento
                    </button>
                  ) : null}
                  {String(
                    selectedEvent?.fileUrl ||
                      selectedEvent?.event?.payload?.fileUrl ||
                      resolvedEventUrl ||
                      "",
                  ).trim() || String(selectedEvent?.documentId || "").trim() ? (
                    <button
                      type="button"
                      onClick={() => openEventDocument(selectedEvent)}
                      disabled={resolvingEventUrl}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:cursor-wait disabled:opacity-60"
                    >
                      {resolvingEventUrl
                        ? "Carregando documento..."
                        : "Abrir Documento"}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={handleRequestConsent}
                    disabled={consentSaving || selectedConsentAction.disabled}
                    className={
                      "w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold border " +
                      (consentSaving
                        ? "border-amber-200 bg-amber-100 text-amber-800 cursor-wait"
                        : selectedConsentAction.className)
                    }
                    title={selectedConsentAction.label}
                  >
                    {consentSaving
                      ? "Solicitando..."
                      : selectedConsentAction.label}
                  </button>
                  <button
                    type="button"
                    onClick={togglePinned}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                    aria-label={
                      selectedEvent?.isPinned ? "Desafixar" : "Fixar Nota"
                    }
                    title={selectedEvent?.isPinned ? "Desafixar" : "Fixar Nota"}
                  >
                    <Pin
                      size={16}
                      className={
                        selectedEvent?.isPinned
                          ? "fill-blue-600 text-blue-600"
                          : "text-slate-400"
                      }
                    />
                    {selectedEvent?.isPinned ? "Desafixar Nota" : "Fixar Nota"}
                  </button>

                  {consentFeedback ? (
                    <div className="text-xs font-semibold text-slate-600 whitespace-pre-wrap text-center">
                      {consentFeedback}
                    </div>
                  ) : null}

                  <textarea
                    ref={replyTextareaRef}
                    value={replyDraft}
                    onChange={(e) =>
                      setReplyDraft(String(e.target.value || ""))
                    }
                    onKeyDown={(e) => {
                      if (e.key !== "Enter") return;
                      if (e.shiftKey) return;
                      if (e.isComposing) return;
                      e.preventDefault();
                      if (replySaving) return;
                      if (!String(replyDraft || "").trim()) return;
                      addReply();
                    }}
                    placeholder="Adicionar comentário a este registro..."
                    className="w-full min-h-[72px] px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:border-blue-300"
                  />
                  <div className="flex justify-end">
                    <button
                      type="button"
                      disabled={replySaving || !String(replyDraft || "").trim()}
                      onClick={addReply}
                      className={
                        "inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border " +
                        (replySaving || !String(replyDraft || "").trim()
                          ? "border-slate-200 bg-slate-100 text-slate-400"
                          : "border-blue-700 bg-blue-600 text-white hover:bg-blue-700")
                      }
                    >
                      {replySaving ? "Enviando..." : "Responder"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};
